import type {
  TimelineAnnotation,
  TimelineAnnotationAuthoringSource,
  TimelineAnnotationProvenance,
} from "@/state/timeline/timeline.types";

import type {
  AnnotationCaptureSource,
  AnnotationRecord,
  AnnotationSuggestion,
  AnnotationSurfaceKind,
} from "./annotationSurface.types";

export interface AnnotationSuggestionContext {
  suggestionId: string;
  suggestionSource: "inspector" | "panel" | "auto";
  suggestionDisposition: "accepted" | "merged" | "tweaked";
  generatedAt: number | null;
  originalSuggestionText: string;
}

function deriveAuthoringSource(
  captureSource: AnnotationCaptureSource,
  suggestionContext?: AnnotationSuggestionContext | null,
): TimelineAnnotationAuthoringSource {
  if (captureSource === "voice") return "dictated";
  if (captureSource === "ai" || suggestionContext) return "ai_assisted";
  return "human";
}

export function buildAnnotationProvenance(
  captureSource: AnnotationCaptureSource,
  suggestionContext?: AnnotationSuggestionContext | null,
): TimelineAnnotationProvenance {
  return {
    authoringSource: deriveAuthoringSource(captureSource, suggestionContext),
    captureSource,
    suggestionId: suggestionContext?.suggestionId ?? null,
    suggestionSource: suggestionContext?.suggestionSource ?? null,
    suggestionDisposition: suggestionContext?.suggestionDisposition ?? null,
    generatedAt: suggestionContext?.generatedAt ?? null,
    originalSuggestionText: suggestionContext?.originalSuggestionText ?? null,
  };
}

export function buildPublishedAnnotationRecord(input: {
  kind: AnnotationSurfaceKind;
  captureSource: AnnotationCaptureSource;
  assetLabel: string;
  clipId: string | null;
  linkedAudio: boolean;
  frameIndex: number;
  anchorTimecode: string;
  timeSec: number | null;
  rawNote: string;
  nextAction: string | null;
  markerVisible?: boolean;
  suggestionContext?: AnnotationSuggestionContext | null;
  createdAt?: number;
}): AnnotationRecord {
  const createdAt = input.createdAt ?? Date.now();
  const provenance = buildAnnotationProvenance(input.captureSource, input.suggestionContext);

  return {
    id: `annotation-${createdAt.toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt,
    updatedAt: createdAt,
    type: input.kind,
    assetLabel: input.assetLabel,
    clipId: input.clipId,
    linkedAudio: input.linkedAudio,
    frameIndex: input.frameIndex,
    anchorTimecode: input.anchorTimecode,
    timeSec: input.timeSec,
    rawNote: input.rawNote,
    nextAction: input.nextAction,
    markerVisible: input.markerVisible ?? true,
    provenance,
    action: input.nextAction,
    source: input.captureSource,
  };
}

export function resolveCanonicalAnnotationRecord(input: {
  existingAnnotation?: AnnotationRecord | null;
  kind: AnnotationSurfaceKind;
  captureSource: AnnotationCaptureSource;
  assetLabel: string;
  clipId: string | null;
  linkedAudio: boolean;
  frameIndex: number;
  anchorTimecode: string;
  timeSec: number | null;
  rawNote: string;
  nextAction: string | null;
  markerVisible?: boolean;
  suggestionContext?: AnnotationSuggestionContext | null;
  timestamp?: number;
}): AnnotationRecord {
  const timestamp = input.timestamp ?? Date.now();
  const markerVisible = input.markerVisible ?? true;

  if (!input.existingAnnotation) {
    return buildPublishedAnnotationRecord({
      kind: input.kind,
      captureSource: input.captureSource,
      assetLabel: input.assetLabel,
      clipId: input.clipId,
      linkedAudio: input.linkedAudio,
      frameIndex: input.frameIndex,
      anchorTimecode: input.anchorTimecode,
      timeSec: input.timeSec,
      rawNote: input.rawNote,
      nextAction: input.nextAction,
      markerVisible,
      suggestionContext: input.suggestionContext,
      createdAt: timestamp,
    });
  }

  return {
    ...input.existingAnnotation,
    updatedAt: timestamp,
    type: input.kind,
    summary: input.rawNote,
    assetLabel: input.assetLabel,
    clipId: input.clipId,
    linkedAudio: input.linkedAudio,
    frameIndex: input.frameIndex,
    anchorTimecode: input.anchorTimecode,
    timeSec: input.timeSec,
    rawNote: input.rawNote,
    nextAction: input.nextAction,
    markerVisible,
    provenance: {
      ...(input.existingAnnotation.provenance ?? {}),
      ...buildAnnotationProvenance(input.captureSource, input.suggestionContext),
    },
    action: input.nextAction,
    source: input.captureSource,
  };
}

export function buildAnnotationUpdatePatch(annotation: AnnotationRecord): Partial<TimelineAnnotation> {
  return {
    updatedAt: annotation.updatedAt,
    type: annotation.type,
    summary: annotation.summary,
    assetLabel: annotation.assetLabel,
    clipId: annotation.clipId,
    linkedAudio: annotation.linkedAudio,
    frameIndex: annotation.frameIndex,
    anchorTimecode: annotation.anchorTimecode,
    timeSec: annotation.timeSec,
    rawNote: annotation.rawNote,
    nextAction: annotation.nextAction,
    markerVisible: annotation.markerVisible,
    provenance: annotation.provenance,
    action: annotation.action,
    source: annotation.source,
  };
}

export function suggestionActionLabel(record: AnnotationSuggestion): string | null {
  const action = String(record.nextAction ?? record.action ?? "").trim();
  if (action) return action;

  const proposed = String(record.proposedAction ?? "").trim();
  return proposed ? proposed.replace(/_/g, " ") : null;
}

export function materializeSuggestionDraft(suggestion: AnnotationSuggestion, disposition: AnnotationSuggestionContext["suggestionDisposition"]) {
  return {
    kind: suggestion.type,
    draftNote: suggestion.summary ?? suggestion.rawNote,
    draftNextAction: suggestionActionLabel(suggestion) ?? "",
    sourceOverride: "ai" as const,
    suggestionContext: {
      suggestionId: suggestion.id,
      suggestionSource: suggestion.suggestionSource ?? "panel",
      suggestionDisposition: disposition,
      generatedAt: suggestion.queuedAt ?? suggestion.createdAt ?? null,
      originalSuggestionText: suggestion.rawNote,
    },
  };
}

function normalizeMergedSegment(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function appendDistinctSegment(currentValue: string, nextValue: string): string {
  const current = currentValue.trim();
  const next = nextValue.trim();

  if (!next) return currentValue;
  if (!current) return next;

  const normalizedCurrent = normalizeMergedSegment(current);
  const normalizedNext = normalizeMergedSegment(next);

  if (!normalizedNext) return currentValue;
  if (normalizedCurrent === normalizedNext || normalizedCurrent.includes(normalizedNext)) {
    return current;
  }

  return `${current} ${next}`;
}

export function mergeSuggestionIntoDraft(
  currentDraftNote: string,
  currentDraftNextAction: string,
  suggestion: AnnotationSuggestion,
) {
  const mergedNote = suggestion.summary ?? suggestion.rawNote;
  const mergedAction = suggestionActionLabel(suggestion) ?? "";

  return {
    kind: suggestion.type,
    draftNote: appendDistinctSegment(currentDraftNote, mergedNote),
    draftNextAction: appendDistinctSegment(currentDraftNextAction, mergedAction),
    sourceOverride: "ai" as const,
    suggestionContext: {
      suggestionId: suggestion.id,
      suggestionSource: suggestion.suggestionSource ?? "panel",
      suggestionDisposition: "merged" as const,
      generatedAt: suggestion.queuedAt ?? suggestion.createdAt ?? null,
      originalSuggestionText: suggestion.rawNote,
    },
  };
}